import { css } from "@emotion/react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { IconButton, Link, ServiceIcon, Text } from "@zapier/design-system";

import { LOG_STREAMS_PATH } from "@zapier/reporting/src/constants";
import { useLocalStorage } from "@zapier/reporting/src/hooks";

const STORAGE_KEY_PREFIX = "zap-runs:log-streams-promotion:dismissed:v1";
const CONNECTOR_SRC = new URL("./assets/connector.svg", import.meta.url).href;
const ZAPIER_WORDMARK_SRC = new URL(
  "./assets/zapier-wordmark.svg",
  import.meta.url
).href;

const SERVICE_ICONS = [
  {
    name: "Microsoft",
    src: new URL("./assets/microsoft.png", import.meta.url).href,
  },
  {
    name: "Splunk",
    src: new URL("./assets/splunk.png", import.meta.url).href,
  },
  {
    name: "Grafana",
    src: new URL("./assets/grafana.png", import.meta.url).href,
  },
  {
    name: "Datadog",
    src: new URL("./assets/datadog.png", import.meta.url).href,
  },
] as const;

const containerStyle = css`
  container: log-streams-promotion / inline-size;
  min-inline-size: 0;
`;

const rootStyle = css`
  align-items: center;
  background: var(--zds-background-weaker);
  border: var(--zds-border-width-default) solid var(--zds-stroke-weaker);
  border-radius: var(--zds-radius-large);
  box-shadow: var(--zds-shadow-1);
  box-sizing: border-box;
  column-gap: var(--zds-space-16);
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  inline-size: 100%;
  margin-block-end: var(--zds-space-20);
  overflow: hidden;
  padding: var(--zds-space-24);
  position: relative;

  @container log-streams-promotion (max-width: 749px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const contentStyle = css`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: var(--zds-space-12);
  min-inline-size: 0;
`;

const copyStyle = css`
  min-inline-size: 0;
`;

const ctaStyle = css`
  flex-shrink: 0;
`;

const illustrationStyle = css`
  align-items: center;
  align-self: stretch;
  background: var(--zds-background-default);
  border-radius: var(--zds-radius-large);
  box-sizing: border-box;
  display: flex;
  gap: calc(var(--zds-space-12) - var(--zds-space-2));
  inline-size: calc(var(--zds-space-40) * 7);
  justify-content: center;
  padding: calc(var(--zds-space-12) - var(--zds-space-2)) var(--zds-space-24);

  @container log-streams-promotion (max-width: 749px) {
    display: none;
  }
`;

const zapierLogoStyle = css`
  align-items: center;
  background: var(--zds-brand-orange);
  block-size: var(--zds-space-36);
  border-radius: var(--zds-radius-default);
  display: flex;
  flex: 0 0 var(--zds-space-36);
  justify-content: center;

  img {
    inline-size: 82.5%;
  }
`;

const connectorStyle = css`
  flex: 1 1 auto;
  min-inline-size: 0;
`;

const serviceIconsStyle = css`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
`;

const serviceIconStyle = css`
  display: flex;

  &:not(:last-child) {
    margin-inline-end: calc(var(--zds-space-12) * -1);
  }
`;

const dismissButtonStyle = css`
  inset-block-start: var(--zds-space-6);
  inset-inline-end: var(--zds-space-6);
  position: absolute;
`;

function PromotionIllustration() {
  return (
    <div aria-hidden={true} css={illustrationStyle}>
      <span css={zapierLogoStyle}>
        <img alt="" src={ZAPIER_WORDMARK_SRC} />
      </span>
      <img alt="" css={connectorStyle} src={CONNECTOR_SRC} />
      <span css={serviceIconsStyle}>
        {SERVICE_ICONS.map((service) => (
          <span css={serviceIconStyle} key={service.name}>
            <ServiceIcon
              aria-hidden={true}
              hasBorder={true}
              serviceName={service.name}
              size="medium"
              src={service.src}
            />
          </span>
        ))}
      </span>
    </div>
  );
}

interface LogStreamsPromotionProps {
  readonly accountId: string;
  readonly userId: string;
}

interface HydratedLogStreamsPromotionProps {
  readonly storageKey: string;
}

function HydratedLogStreamsPromotion({
  storageKey,
}: HydratedLogStreamsPromotionProps) {
  const t = useTranslations("history-page.log-streams-promotion");
  const [isDismissedForSession, setIsDismissedForSession] = useState(false);
  const [isDismissed, setIsDismissed] = useLocalStorage(storageKey, false, {
    serverValue: true,
  });

  if (isDismissed || isDismissedForSession) {
    return undefined;
  }

  const handleDismiss = () => {
    setIsDismissedForSession(true);
    setIsDismissed(true);
  };

  return (
    <div css={containerStyle}>
      <section
        aria-label={t("region-label")}
        css={rootStyle}
        data-custom-component="reporting::log-streams-promotion"
      >
        <div css={contentStyle}>
          <div css={copyStyle}>
            <Text tag="p" type="Body3">
              {t("heading")}
            </Text>
            <Text tag="p" type="Body1Multiline">
              {t("body")}
            </Text>
          </div>
          <Link
            css={ctaStyle}
            href={LOG_STREAMS_PATH}
            size="compact"
            variant="secondary"
          >
            {t("cta")}
          </Link>
        </div>
        <PromotionIllustration />
        <div css={dismissButtonStyle}>
          <IconButton
            aria-label={t("dismiss")}
            name="formX"
            onPress={handleDismiss}
            size="small"
            variant="ghost"
          />
        </div>
      </section>
    </div>
  );
}

export function LogStreamsPromotion({
  accountId,
  userId,
}: LogStreamsPromotionProps) {
  const storageKey = `${STORAGE_KEY_PREFIX}:${userId}:${accountId}`;

  return (
    <HydratedLogStreamsPromotion key={storageKey} storageKey={storageKey} />
  );
}
